// What may stop a reply's Send, and what only warns (2026-10-30, Jenn;
// docs/decisions/2026-10-30-send-warns-not-blocks.md, docs/contact-view.md
// "What can stop Send").
//
// Send (and Retry Send) is stopped only by:
//   - an unfilled placeholder in the reply (findUnresolvedPlaceholders():
//     INSERT… tokens, [AGENT_FIRST_NAME]-style tokens, a redaction token, an
//     RMA reference with no number to give it);
//   - a link in a Walmart reply;
//   - an empty reply (nothing but the signature);
//   - no address to send to (an order-only notice with no customer To, a
//     reply with no send target).
// Every other check is a warning: the CSR sees all of them in one dialog, in
// plain words, each with its fix when there is one, and can Send anyway. Send
// anyway sends the reply exactly as written and processes nothing. The
// request then carries the codes the CSR acknowledged
// (acknowledgedWarnings); the server refuses a warning only while its code is
// not acknowledged (409 send_warnings, with the list), and records Send
// anyway in the Activity Log (sendCheckEntry()) and on the version stamp,
// with the codes and who, never the reply text.
//
// A warning: { code, text, fixes?, index?, kind? }.
//   code   a stable snake_case code (SEND_WARNING below, or another branch's
//          own, such as an amount on no step); the acknowledgement names it;
//   text   what the CSR reads;
//   fixes  [{ kind, label, index? }], optional: the buttons beside it
//          (SEND_FIX). The panel runs the fix; Send anyway ignores them.
// New warnings join by adding a code here and pushing to the panel's list
// (SuggestionPanel.jsx's sendWarningsNow()); the dialog
// (ui/src/SendWarningsDialog.jsx) shows any list.
// Pure: no calls.

export const SEND_STOP = Object.freeze({
  placeholder: 'unfilled_placeholder',
  walmartLink: 'walmart_link',
  emptyReply: 'empty_reply',
  noAddress: 'no_address',
})
export const SEND_STOP_CODES = Object.freeze(Object.values(SEND_STOP))

export const SEND_WARNING = Object.freeze({
  promiseStepUnprocessed: 'promise_step_unprocessed',
  promiseWithoutStep: 'promise_without_step',
  promiseStepUnchecked: 'promise_step_unchecked',
  promiseDoneStepPending: 'promise_done_step_pending',
  // The reply says it already happened, no step of its kind is on the
  // contact, and the order shows none (2026-10-30, Jenn).
  promiseDoneNoStep: 'promise_done_no_step',
  // "Signifyd" (internal only) in the reply (2026-10-30, Jenn: a warning;
  // only an acknowledged human send passes the clients' guard).
  signifydMention: 'signifyd_mention',
  // An Invoice Request reply with no invoice to attach (2026-10-30, Jenn):
  // known only at the send, so the send answers it and Retry Send carries
  // the acknowledgement.
  invoiceRequestNoInvoice: 'invoice_request_no_invoice',
  taskPathNotChosen: 'task_path_not_chosen',
  invoiceClaim: 'invoice_claim',
  taskSubjectMissing: 'task_subject_missing',
  rmaReference: 'rma_reference',
  recentGroupReply: 'recent_group_reply',
  regeneratedCompare: 'regenerated_compare_open',
})

// The Signifyd warning's words.
export const SIGNIFYD_SEND_WARNING = "The reply mentions Signifyd, an internal fraud-check service. Customers shouldn't see it. Remove it, or Send anyway."

// The 409 the decision and retry-send routes answer while a warning is not
// acknowledged: { error: SEND_WARNINGS_ERROR, warnings }.
export const SEND_WARNINGS_ERROR = 'send_warnings'

// The fixes a warning can offer.
export const SEND_FIX = Object.freeze({
  processStep: 'process_step',
  markNotNeeded: 'mark_not_needed',
  checkStep: 'check_step',
  choosePath: 'choose_path',
  addAttachment: 'add_attachment',
  enterSubject: 'enter_subject',
  // The agent's own steps (2026-10-30, ui/src/agentReplyFollowsSteps.js):
  // the reply's amount replaced by the step's.
  useStepAmount: 'use_step_amount',
})

const CODE_RE = /^[a-z][a-z0-9_]{0,59}$/

export function sendWarning(code, text, { fixes = [], index = undefined, kind = undefined } = {}) {
  return {
    code,
    text,
    ...(Number.isInteger(index) ? { index } : {}),
    ...(kind ? { kind } : {}),
    fixes: (fixes ?? []).filter((f) => f && f.kind && f.label),
  }
}

// The acknowledged codes from a request body: well-formed codes only, once
// each, at most 40. A stop code is never acknowledgeable and is dropped.
export function cleanAcknowledgedWarnings(list) {
  const out = []
  for (const c of Array.isArray(list) ? list : []) {
    if (typeof c !== 'string' || !CODE_RE.test(c) || SEND_STOP_CODES.includes(c) || out.includes(c)) continue
    out.push(c)
    if (out.length >= 40) break
  }
  return out
}

// The warnings whose code is not acknowledged.
export function unacknowledgedWarnings(warnings, acknowledged = []) {
  const ack = new Set(acknowledged ?? [])
  return (warnings ?? []).filter((w) => w?.code && !ack.has(w.code))
}

// One list from several (the panel's own and the server's): one per code
// and kind / step.
export function mergeSendWarnings(...lists) {
  const out = []
  for (const w of lists.flat()) {
    if (!w?.code) continue
    const key = `${w.code}|${w.kind ?? ''}|${w.index ?? ''}`
    if (out.some((o) => `${o.code}|${o.kind ?? ''}|${o.index ?? ''}` === key)) continue
    out.push(w)
  }
  return out
}

export const SEND_CHECK_OUTCOMES = Object.freeze(['warned', 'sent_anyway', 'refused'])
export const SEND_CHECK_PHASE = 'send_check'

// The Activity Log line for a Send check (ids and codes only, never the reply
// text or order data): outcome 'warned' (the server answered 409
// send_warnings), 'sent_anyway' (sent past acknowledged warnings) or
// 'refused' (a stop at Send). route: 'decision' or 'retry_send' (or 'send'
// for a refusal inside the send itself).
export function sendCheckEntry({ outcome, codes = [], route = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const list = [...new Set((codes ?? []).filter((c) => typeof c === 'string' && CODE_RE.test(c)))]
  const words = outcome === 'sent_anyway' ? 'Sent anyway past' : outcome === 'refused' ? 'Send refused:' : 'Send warned:'
  return {
    service: 'CXHub',
    method: 'NOTE',
    endpoint: null,
    headers: null,
    label: `${words} ${list.join(', ') || 'no code'}`,
    phase: SEND_CHECK_PHASE,
    sendCheck: { outcome, codes: list, ...(route ? { route } : {}) },
    actedBy,
    at,
  }
}

// The version stamp's record of a Send anyway: the codes, when, the route and
// the sender's role class. No person is named on the stamp (the QA feed
// carries it); the Activity Log line names who.
export function sendAnywayStamp({ codes = [], route = null, roles = [], at = new Date().toISOString() } = {}) {
  return {
    codes: [...new Set((codes ?? []).filter((c) => typeof c === 'string' && CODE_RE.test(c)))],
    at,
    route,
    by: (roles ?? []).includes('admin') ? 'admin' : 'csr',
  }
}
